import { SEASON_END_MONTH, SEASON_START_MONTH } from '@/lib/common'
import { DisasterDatum, GeorefData, TSDatum } from '@/lib/types'
import { differenceInDays, getDayOfYear, parseISO } from 'date-fns'
import { GeoJSON } from 'leaflet'
import { MutationTree } from 'vuex'
import * as s from './state'

export enum MutationTypes {
	SET_LANGUAGE = 'SET_LANGUAGE',
	SET_CB_DATA = 'SET_CB_DATA',
	SET_TS_DATA = 'SET_TS_DATA',
	SET_GEOREF_DATA = 'SET_GEOREF_DATA',
	SET_DISASTER_DATA = 'SET_DISASTER_DATA',
	SET_SELECTED_EVENT = 'SET_SELECTED_EVENT',
	SET_SELECTED_FEATURE = 'SET_SELECTED_FEATURE',
	SET_SELECTED_SEASON = 'SET_SELECTED_SEASON',
	SET_SHOW_RAINFALL = 'SET_SHOW_RAINFALL',
	SET_TREND_VIEW = 'SET_TREND_VIEW',
	SET_SELECTED_VIEW = 'SET_SELECTED_VIEW',
	SET_SHOW_OVERVIEW = 'SET_SHOW_OVERVIEW',
	SET_SHOW_OVERVIEW_ON_START = 'SET_SHOW_OVERVIEW_ON_START',
	SET_PLAYING = 'SET_PLAYING',
	SET_LOADING = 'SET_LOADING',
	SET_FINISHED_LOADING = 'SET_FINISHED_LOADING'
}

export type Mutations<S = s.State> = {
	[MutationTypes.SET_LANGUAGE](state: S, payload: s.LanguageType): void
	[MutationTypes.SET_CB_DATA](state: S, payload: GeoJSON): void
	[MutationTypes.SET_TS_DATA](state: S, payload: TSDatum[]): void
	[MutationTypes.SET_GEOREF_DATA](state: S, payload: GeorefData): void
	[MutationTypes.SET_DISASTER_DATA](state: S, payload: DisasterDatum[]): void
	[MutationTypes.SET_SELECTED_EVENT](
		state: S,
		payload: { fcIndex: number; color?: string }
	): void
	[MutationTypes.SET_SELECTED_FEATURE](
		state: S,
		payload: { fcIndex: number; fIndex: number; color?: string }
	): void
	[MutationTypes.SET_SELECTED_SEASON](state: S, payload: number): void
	[MutationTypes.SET_SHOW_RAINFALL](state: S, payload: boolean): void
	[MutationTypes.SET_TREND_VIEW](state: S, payload: s.TrendView): void
	[MutationTypes.SET_SELECTED_VIEW](state: S, payload: s.ViewType): void
	[MutationTypes.SET_SHOW_OVERVIEW](state: S, payload: boolean): void
	[MutationTypes.SET_SHOW_OVERVIEW_ON_START](state: S, payload: boolean): void
	[MutationTypes.SET_PLAYING](state: S, payload: boolean): void
	[MutationTypes.SET_LOADING](state: S): void
	[MutationTypes.SET_FINISHED_LOADING](state: S): void
}

export const mutations: MutationTree<s.State> & Mutations = {
	[MutationTypes.SET_LANGUAGE](state, payload) {
		state.language = payload
	},
	[MutationTypes.SET_CB_DATA](state, payload) {
		state.cloudData = payload
		// @ts-ignore
		state.cloudData?.features.forEach(fc => {
			// @ts-ignore
			fc?.features.forEach(feature => {
				feature.properties.date = parseISO(`${feature.properties.date}`)
			})
		})
	},
	[MutationTypes.SET_TS_DATA](state, payload) {
		state.tsData = payload
		if (state.tsData !== null) {
			state.tsData = state.tsData.map(d => ({
				date: parseISO(`${d.date}Z`),
				rain: d.rain
			}))
			const startYear = getDayOfYear(state.tsData[0].date)
			const daysPerSeason = differenceInDays(
				new Date(startYear + 1, SEASON_END_MONTH - 1, 30),
				new Date(startYear, SEASON_START_MONTH - 1, 1)
			)

			const meanTotals: number[] = []
			const meanSums: number[] = []
			for (let i = 0; i < daysPerSeason; i++) {
				meanTotals[i] = 0
				meanSums[i] = 0
			}

			for (let i = 0; i < state.tsData.length; i++) {
				if (
					state.tsData[i].date.getMonth() == SEASON_START_MONTH - 1 &&
					state.tsData[i].date.getDay() == 1 &&
					state.tsData.length > i + daysPerSeason
				) {
					// We have started a new season.
					for (let j = 0; j < daysPerSeason; j++) {
						const d = state.tsData[i + j]
						meanTotals[j] += d.rain
						meanSums[j] += 1
					}
					i += daysPerSeason - 1
				}
			}

			state.tsClimatology = []
			for (let i = 0; i < daysPerSeason; i++) {
				state.tsClimatology.push(meanTotals[i] / meanSums[i])
			}
		}
	},
	[MutationTypes.SET_GEOREF_DATA](state, payload) {
		state.georefData = payload
	},
	[MutationTypes.SET_DISASTER_DATA](state, payload) {
		state.disasterData = payload
		state.disasterData = state.disasterData.sort((a, b) =>
			a.date < b.date ? -1 : 1
		)
		state.date2DisasterIndex = new Map()
		state.disasterData.forEach((d, i) => {
			if (state.date2DisasterIndex.has(d.date.getTime())) {
				const disasterArray = state.date2DisasterIndex.get(d.date.getTime())
				if (disasterArray) {
					disasterArray.push(i)
				} else {
					console.log('something weird is going on')
				}
			} else {
				state.date2DisasterIndex.set(d.date.getTime(), [i])
			}
		})
	},
	[MutationTypes.SET_SELECTED_EVENT](state, payload) {
		// @ts-ignore
		state.selectedEvent = state.cloudData?.features[payload.fcIndex]
		state.selectedEventIndex = payload.fcIndex
		state.selectedFeature =
			// @ts-ignore
			state.cloudData?.features[payload.fcIndex]?.features[0]
		state.selectedFeatureIndex = 0
		if (payload.color) {
			state.color = payload.color
		}
	},
	[MutationTypes.SET_SELECTED_FEATURE](state, payload) {
		// @ts-ignore
		state.selectedEvent = state.cloudData?.features[payload.fcIndex]
		state.selectedFeature =
			// @ts-ignore
			state.cloudData?.features[payload.fcIndex]?.features[payload.fIndex]
		state.selectedFeatureIndex = payload.fIndex
		if (payload.color) {
			state.color = payload.color
		}
	},
	[MutationTypes.SET_SELECTED_SEASON](state, payload) {
		state.selectedSeasonIndex = payload
	},
	[MutationTypes.SET_SHOW_RAINFALL](state, payload) {
		state.showRainfall = payload
	},
	[MutationTypes.SET_TREND_VIEW](state, payload) {
		state.trendView = payload
	},
	[MutationTypes.SET_SELECTED_VIEW](state, payload) {
		state.selectedView = payload
	},
	[MutationTypes.SET_SHOW_OVERVIEW](state, payload) {
		state.showOverview = payload
	},
	[MutationTypes.SET_SHOW_OVERVIEW_ON_START](state, payload) {
		state.showOverviewOnStart = payload
		document.cookie = `showOverviewOnStart=${payload}`
	},
	[MutationTypes.SET_PLAYING](state, payload) {
		state.playing = payload
	},
	[MutationTypes.SET_LOADING](state) {
		state.loadingCount++
	},
	[MutationTypes.SET_FINISHED_LOADING](state) {
		if (state.loadingCount > 0) {
			state.loadingCount--
		}
	}
}
